/* eslint-disable complexity */
<template>
  <div id="gs-product-wizard-root" class="gs-app gs-product-wizard">
    <portal to="headerBreadCrumb">
      <button
        type="button"
        class="gs-product-wizard-shell__back"
        @click="sendToFunnelProducts"
      >
        <gs-icon name="arrow-left" size="16"></gs-icon>
        <span>{{ edit_product ? 'Edit product' : 'Add product' }}</span>
      </button>
    </portal>

    <portal to="headerContent">
      <div class="gs-product-wizard-shell__actions">
        <gs-button
          v-if="isPreviouslyCompletedProduct && currentTab !== 'finish-step' && !loadError"
          variant="outline"
          :disabled="isSaving"
          @click="exitWizardForCompletedProducts()"
        >Save & Exit</gs-button>
        <gs-button
          v-if="!loadError"
          variant="primary"
          :loading="isSaving"
          @click="currentTab === 'finish-step' ? saveAndExitWizard() : requestStepSave(true)"
        >{{ currentTab === 'finish-step' ? $t('frontend_products_create_save_exit') : 'Save & Continue' }}</gs-button>
      </div>
    </portal>

    <v-wait for="create_product">
      <div v-if="loadError" class="gs-product-wizard__load-error">
        <gs-card>
          <div class="gs-product-wizard__load-error-icon">
            <gs-icon name="x" size="24"></gs-icon>
          </div>
          <h1>We couldn't open this product</h1>
          <p>{{ loadError }}</p>
          <div class="gs-product-wizard__load-error-actions">
            <gs-button variant="primary" @click="retryProductWizard">Try again</gs-button>
            <gs-button variant="outline" @click="sendToFunnelProducts">Back to products</gs-button>
          </div>
        </gs-card>
      </div>

      <div v-else class="gs-product-wizard__body">
        <aside class="gs-product-wizard__sidebar">
          <div
            v-for="group in wizardNavGroups"
            :key="group.title"
            class="gs-product-wizard__nav-group"
          >
            <div class="gs-product-wizard__nav-heading">
              <span>{{ group.title }}</span>
              <span
                class="gs-product-wizard__group-progress"
                :class="{
                  'is-complete': isWizardGroupComplete(group),
                  'has-error': wizardGroupHasError(group),
                }"
                :aria-label="wizardGroupProgressLabel(group)"
              >
                <gs-icon v-if="isWizardGroupComplete(group)" name="check" size="14"></gs-icon>
                <gs-icon v-else-if="wizardGroupHasError(group)" name="x" size="14"></gs-icon>
                <span v-else>{{ wizardGroupCompletedCount(group) }} / {{ group.steps.length }}</span>
              </span>
            </div>
            <button
              v-for="step in group.steps"
              :key="step.value"
              type="button"
              class="gs-product-wizard__nav-item"
              :class="{
                'is-active': currentTab === step.value,
                'is-complete': isWizardStepComplete(step.value),
                'has-error': isWizardStepError(step.value),
              }"
              :aria-current="currentTab === step.value ? 'step' : null"
              @click="handleTabChange(step.value)"
            >
              <span class="gs-product-wizard__nav-dot" aria-hidden="true"></span>
              <span>{{ step.label }}</span>
            </button>
            <button
              v-if="group.title === 'Links' && product && product.id"
              type="button"
              class="gs-product-wizard__nav-item gs-product-wizard__nav-action"
              @click="openCheckoutLinksShelf"
            >
              <span class="gs-product-wizard__nav-dot gs-product-wizard__nav-dot--action">
                <gs-icon name="external-link" size="12"></gs-icon>
              </span>
              <span>Checkout Links</span>
            </button>
          </div>
        </aside>

        <main class="gs-product-wizard__main">
          <div class="gs-product-wizard__intro">
            <span class="gs-product-wizard__step-count">Step {{ currentTabIndex + 1 }} of {{ tabs.length }}</span>
            <h1
              id="gs-product-wizard-title"
              class="gs-product-wizard__title"
            >{{ currentWizardStep.heading }}</h1>
            <p>{{ currentWizardStep.description }}</p>
          </div>

          <div
            v-if="validationNoticeTab === currentTab"
            class="gs-product-wizard__validation-notice"
            role="alert"
            aria-live="assertive"
          >
            <span class="gs-product-wizard__validation-icon">
              <gs-icon name="x" size="12"></gs-icon>
            </span>
            <div>
              <strong>This step still needs attention</strong>
              <p>{{ validationNoticeMessage }}</p>
            </div>
          </div>

          <div class="gs-product-wizard__canvas gs-stack">
            <component :is="currentTab" :ref="currentTab" :model="model" :gateway-check-box="gatewayCheckBox"
              :gateway-integrations="gatewayIntegrations" :product="product" :price-points="price_points"
              :resources-path="resourcesPath" :server-path="serverPath" :plugin-site-data="pluginSiteData"
              :available-integrations="availableIntegrations" :funnel-products="funnelProducts"
              :facebookCoversionList="this.facebook_coversion_list" :affiliates="affiliates"
              @updateFacebookPixels="facebook_coversion_list = $event"
              @saveProdInfo="saveProductFromChild" @updateModel="handleModelUpdate"
              @orderPersistStart="beginOrderPersist" @orderPersistEnd="endOrderPersist"
              @updateGatewayCheckbox="gatewayCheckBox = $event"
              @on-validate="stepValidationSuccessful(currentTab, $event)" @updatePricePoints="handlePricePointsUpdate"
              @updateIntegration="gatewayIntegrations = $event" @reloadIntegrations="fetchIntegrations" />
          </div>

          <div class="gs-product-wizard__footer-actions">
            <div class="gs-product-wizard__footer-start">
              <gs-button
                v-if="currentTab != 'info-step'"
                variant="outline"
                :disabled="isSaving"
                @click="moveOnWizard(false)"
              >{{ $t('frontend_products_create_tabs_back') }}</gs-button>
            </div>
            <div class="gs-product-wizard__save-status" aria-live="polite">
              <span :class="{ 'is-dirty': isCurrentStepDirty }"></span>
              {{ currentStepSaveStatus }}
            </div>
            <div class="gs-product-wizard__footer-end">
              <gs-button
                variant="outline"
                :loading="isSaving && !pendingAdvance"
                :disabled="isSaving"
                @click="requestStepSave(false)"
              >Save</gs-button>
              <gs-button
                v-if="currentTab != 'finish-step'"
                variant="primary"
                :loading="isSaving && pendingAdvance"
                :disabled="isSaving"
                @click="requestStepSave(true)"
              >Save & Continue</gs-button>
              <gs-button
                v-else
                variant="primary"
                :loading="isSaving"
                :disabled="isSaving"
                @click="saveAndExitWizard"
              >Save & Exit</gs-button>
            </div>
          </div>
        </main>
      </div>
    </v-wait>

    <gs-drawer v-model="showCheckoutLinksShelf" title="Checkout Links" size="wide">
      <div class="gs-product-wizard__drawer-intro">
        <p>Review price-point details or open the shared checkout-link workspace without leaving the wizard.</p>
        <gs-button variant="primary" @click="openCheckoutLinksManager">
          <gs-icon name="external-link"></gs-icon>
          Manage links
        </gs-button>
      </div>

      <div v-if="checkoutLinksLoading" class="gs-product-wizard__shelf-empty">
        Loading checkout links...
      </div>

      <div v-else-if="checkoutLinks.length" class="gs-product-wizard__checkout-list">
        <template v-for="row in checkoutShelfRows">
          <div
            v-if="row.type === 'divider'"
            :key="row.key"
            class="gs-product-wizard__checkout-divider"
          >
            <button
              type="button"
              class="gs-product-wizard__checkout-divider-toggle"
              :aria-expanded="showHiddenCheckoutLinks ? 'true' : 'false'"
              @click="showHiddenCheckoutLinks = !showHiddenCheckoutLinks"
            >
              <gs-icon :name="showHiddenCheckoutLinks ? 'chevron-up' : 'chevron-down'" size="16"></gs-icon>
              <span>More checkout links ({{ row.count }})</span>
            </button>
            <small>These price points sit outside your main pricing list.</small>
          </div>
          <article
            v-else
            :key="row.key"
            class="gs-product-wizard__checkout-card"
          >
            <div class="gs-product-wizard__checkout-card-head">
              <div>
                <strong>{{ row.link.name || 'Checkout link' }}</strong>
                <span>{{ row.link.description || 'Checkout experience' }}</span>
              </div>
              <span class="gs-product-wizard__checkout-type">{{ row.link.type || 'Checkout' }}</span>
            </div>
            <div class="gs-product-wizard__checkout-metadata">
              <span><small>Price points</small>{{ checkoutLinkPricePoints(row.link).length || 'All' }}</span>
              <span><small>Offer details</small>{{ checkoutPricePointSummary(row.link) }}</span>
            </div>
            <div class="gs-product-wizard__checkout-actions">
              <gs-button variant="primary" @click="openCheckoutLinkModal(row.link)">Open checkout link</gs-button>
            </div>
          </article>
        </template>
      </div>

      <div v-else class="gs-product-wizard__shelf-empty">
        <h3>No checkout links yet</h3>
        <p>Create one from the checkout links manager when you are ready.</p>
        <gs-button variant="primary" @click="openCheckoutLinksManager">
          Create checkout link
        </gs-button>
      </div>
    </gs-drawer>

    <modern-checkout-link-modal v-model="showCheckoutLinkModal" :checkout-link="activeCheckoutLink" />

    <gs-toast-provider></gs-toast-provider>

  </div>
</template>

<script>
import axios from 'axios';
import ArrowBracketIcon from '@/assets/images/icons/ArrowBracketIcon.svg';
import ChevronRightIcon from '@/assets/images/icons/ChevronRightIcon.svg';
import {
  required,
  minLength,
  maxLength,
  numeric,
  decimal,
  requiredIf,
  url,
  minValue,
  maxValue,
} from 'vuelidate/lib/validators';
import auth from '@groovepages/gd-lib/auth';
import urlParser from 'js-video-url-parser/lib/base';
import Funnel from '@/models/Funnel';
import Product from '@/models/Product';
import PricePoint from '@/models/PricePoint';
import Integration from '@/models/Integration';
import {
  normalizePricePoint,
  groupCheckoutLinksByPricePoints,
  pricePointsForCheckoutLink,
} from './pricePointGroups';

import AffiliatesStep from './steps/AffiliatesStep';
import ContentsStep from './steps/ContentsStep';
import FinishStep from './steps/FinishStep';
import FunnelStep from './steps/FunnelStep';
import GatewaysStep from './steps/GatewaysStep';
import InfoStep from './steps/InfoStep';
import PricingStep from './steps/PricingStep';
import BumpsStep from './steps/BumpsStep';
import CheckoutStep from './steps/CheckoutStep';
import FullfillmentStep from './steps/FullfillmentStep';
import ProofStep from './steps/ProofStep';
import TrackingStep from './steps/TrackingStep';
import ModernCheckoutLinkModal from './checkout_links/modals/ModernCheckoutLinkModal';


import 'js-video-url-parser/lib/provider/wistia';

import { userRegion } from '@/utils';

const defaultLanguage = (userRegion == 'brazil' ? 'pt' : 'en');

export default {
  layout: 'defaultWithoutSidebar',
  scrollToTop: true,
  middleware: 'auth',
  metaInfo() {
    return { title: this.$t('frontend_products_create_title') };
  },
  validations: {
    model: {
      name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(80),
      },
      internal_name: {
        minLength: minLength(3),
        maxLength: maxLength(500),
      },
      description: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(160),
      },
      sales_page: {
        required,
        url,
      },
      refund_period: {
        required,
        numeric,
        minValue: minValue(0),
      },
      support_info_value: {
        validSupportInfo(value, object) {
          const self = this;

          if (self.model.is_support_enabled) {
            if (value) {
              try {
                const parsed = JSON.parse(value);
                if (parsed && Array.isArray(parsed.channels)) {
                  return parsed.channels.every((channel) => {
                    return channel.name && channel.type && channel.value;
                  });
                }
              } catch (e) {
                // Legacy support info values are validated below.
              }

              if (self.model.support_info_type == 1) {
                var pattern = new RegExp(
                  '^(https?:\\/\\/)?' + // protocol
                  '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
                  '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
                  '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
                  '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
                  '(\\#[-a-z\\d_]*)?$',
                  'i'
                ); // fragment locator
                return !!pattern.test(value);
              }
              var pattern =
                /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
              return pattern.test(value.toLowerCase());
            }
            return false;
          }
          return true;
        },
      },
      waiting_period: {
        required,
        numeric,
        minValue: minValue(0),
      },
      jv_page: {
        url,
      },
      affiliate_landing_pages: {
        required,
        minLength: minLength(1),
        $each: {
          landing_page: {
            url,
          },
          description: {
            required,
            minLength: minLength(3),
            maxLength: maxLength(80),
          },
        },
      },
      paypal_processing_accounts: {
        $each: {
          weightage: {
            required,
            numeric,
            minValue: minValue(0),
            maxValue: maxValue(100),
            totalHundredPercent() {
              const self = this;
              let totalWeightage = 0;

              for (
                let i = 0;
                i < self.model.paypal_processing_accounts.length;
                i++
              ) {
                totalWeightage += parseInt(
                  self.model.paypal_processing_accounts[i].weightage,
                  10
                );
              }

              if (totalWeightage == 100) {
                return true;
              }
              return false;
            },
          },
        },
      },
      credit_card_processing_accounts: {
        $each: {
          weightage: {
            required,
            numeric,
            minValue: minValue(0),
            maxValue: maxValue(100),
            totalHundredPercent() {
              const self = this;
              let totalWeightage = 0;

              for (
                let i = 0;
                i < self.model.credit_card_processing_accounts.length;
                i++
              ) {
                totalWeightage += parseInt(
                  self.model.credit_card_processing_accounts[i].weightage,
                  10
                );
              }

              if (totalWeightage == 100) {
                return true;
              }
              return false;
            },
          },
        },
      },
      pricing_points: {
        required,
        minLength: minLength(1),
        $each: {
          name: {
            required,
            minLength: minLength(3),
            maxLength: maxLength(80),
          },
          internal_name: {
            minLength: minLength(3),
            maxLength: maxLength(500),
          },
          description: {
            required,
            minLength: minLength(5),
            maxLength: maxLength(160),
          },
          minimumQuantity: {
            isValidQuantityType(value, object) {
              if (object.quantity_enable == 1) {
                if (object.minimum_quantity <= object.maximum_quantity) {
                  return true;
                }
                return false;
              }
              return true;
            },
          },
          price: {
            required,
            decimal,
            minValue: minValue(0),
            isValidPricePointPrice(value, object) {
              if (value > 0) {
                return true;
              }
              if (object.is_trial == 1) {
                return false;
              }
              if (object.type == 2 || object.type == 3) {
                return false;
              }
              return true;
            },
          },
          number_of_installments: {
            required: requiredIf((object) => {
              return object.type == 3 || object.type == 4;
            }),
            numeric,
            isValidNumberOfInstallments(value, object) {
              if (object.type == 3 || object.type == 4) {
                if (value < 1) {
                  return false;
                }
                return true;
              }
              return true;
            },
          },
          recurring_interval: {
            required: requiredIf((object) => {
              return object.type == 3 || object.type == 4;
            }),
            isValidNumberOfRicurringInterval(value, object) {
              if (object.type == 3 || object.type == 4) {
                if (value < 1) {
                  return false;
                }
                return true;
              }
              return true;
            },
          },
          recurrence_interval: {
            required: requiredIf((object) => {
              return object.type == 4;
            }),
            numeric,
            isValidNumberOfRecurrenceInterval(value, object) {
              if (object.type == 4) {
                if (value < 1) {
                  return false;
                }
                return true;
              }
              return true;
            },
          },
          trial_price: {
            required: requiredIf((object) => {
              return object.is_trial == 1;
            }),
            decimal,
            minValue: minValue(0),
          },
          trial_period: {
            required: requiredIf((object) => {
              return object.is_trial == 1;
            }),
            numeric,
            minValue: minValue(0),
          },
          post_checkout_details: {
            thankyou_page: {
              required,
              url,
            },
          },
          post_transaction_details: {
            post_transaction_url: {
              required: requiredIf((object) => {
                return object.type == 2;
              }),
              url,
            },
          },
          post_sale_details: {
            $each: {
              link: {
                url,
                required,
              },
            },
          },
          offer_bump_options: {
            $each: {
              heading_line: {
                required: requiredIf((object, value) => {
                  if (object) {
                    return true;
                  }
                  return false;
                }),
              },
              description: {
                required: requiredIf((object, value) => {
                  if (object) {
                    return true;
                  }
                  return false;
                }),
              },
            },
          },
          post_sale_order_bump_options: {
            post_order_heading_line: {
              required: requiredIf((object) => {
                if (
                  object.post_order_bump_enable &&
                  object.post_order_bump_enable == 1
                ) {
                  return true;
                }
                return false;
              }),
            },
            post_order_description: {
              required: requiredIf((object) => {
                if (
                  object.post_order_bump_enable &&
                  object.post_order_bump_enable == 1
                ) {
                  return true;
                }
                return false;
              }),
            },
            post_order_video_details: {
              url: {
                required: requiredIf((object) => {
                  if (
                    object.post_order_show_video &&
                    object.post_order_show_video == true
                  ) {
                    return true;
                  }
                  return false;
                }),
                isValidPostSaleVideoUrl(value, object) {
                  if (
                    object.post_order_show_video &&
                    object.post_order_show_video == true
                  ) {
                    const videoInfo = urlParser.parse(value);
                    if (videoInfo != undefined) {
                      return true;
                    }
                    const brandAltName = this.$store.state.brandData.brandAltName.toLowerCase();
                    const videoAppTitle = this.whitelabelAppData('video').title.toLowerCase();
                    const expression = new RegExp(`(http:|https:|)\/\/?(app\\.${brandAltName}\\.com)\/${videoAppTitle}\/videopage\/[A-Za-z0-9]*\/[A-Za-z0-9]*`, 'g');

                    const regex = new RegExp(expression);
                    if (value.match(regex)) {
                      return true;
                    }

                    return false;
                  }
                  return true;
                },
              },
            },
            post_order_product_id: {
              isValidProductId(value, object) {
                if (
                  object.post_order_bump_enable &&
                  object.post_order_bump_enable == 1 &&
                  (!value || value == 0)
                ) {
                  return false;
                }
                return true;
              },
            },
            post_order_price_point_id: {
              isValidPricePointtId(value, object) {
                if (
                  object.post_order_bump_enable &&
                  object.post_order_bump_enable == 1 &&
                  (!value || value == 0)
                ) {
                  return false;
                }
                return true;
              },
            },
          },
          commission_details: {
            $each: {
              // name:{
              //   required
              // },
              trial_commission: {
                commission: {
                  required,
                  decimal,
                  minValue: minValue(0),
                  isValidCommission(value, object) {
                    const regexp = /^\d+(\.\d{1,2})?$/;

                    if (object.commission_type == 'percent') {
                      return regexp.test(value) && value <= 100;
                    }
                    return regexp.test(value);
                  },
                },
                jv_broker_commission: {
                  required,
                  decimal,
                  minValue: minValue(0),
                  isValidCommission(value, object) {
                    const regexp = /^\d+(\.\d{1,2})?$/;

                    if (object.commission_type == 'percent') {
                      return regexp.test(value) && value <= 100;
                    }
                    return regexp.test(value);
                  },
                },
              },
              sales_commission: {
                commission: {
                  required,
                  decimal,
                  minValue: minValue(0),
                  isValidCommission(value, object) {
                    const regexp = /^\d+(\.\d{1,2})?$/;

                    if (object.commission_type == 'percent') {
                      return regexp.test(value) && value <= 100;
                    }
                    return regexp.test(value);
                  },
                },
                jv_broker_commission: {
                  required,
                  decimal,
                  minValue: minValue(0),
                  isValidCommission(value, object) {
                    const regexp = /^\d+(\.\d{1,2})?$/;

                    if (object.commission_type == 'percent') {
                      return regexp.test(value) && value <= 100;
                    }
                    return regexp.test(value);
                  },
                },
              },
              rebill_commissions: {
                $each: {
                  commission: {
                    required,
                    decimal,
                    minValue: minValue(0),
                    isValidCommission(value, object) {
                      const regexp = /^\d+(\.\d{1,2})?$/;

                      if (object.commission_type == 'percent') {
                        return regexp.test(value) && value <= 100;
                      }
                      return regexp.test(value);
                    },
                  },
                  no_of_payments: {
                    numeric,
                    minValue: minValue(0),
                  },
                  jv_broker_commission: {
                    required,
                    decimal,
                    minValue: minValue(0),
                    isValidCommission(value, object) {
                      const regexp = /^\d+(\.\d{1,2})?$/;

                      if (object.commission_type == 'percent') {
                        return regexp.test(value) && value <= 100;
                      }
                      return regexp.test(value);
                    },
                  },
                },
              },
            },
          },
          legal_options: {
            enabled: 0,
            html: '',
          },
          $trackBy: 'id',
        },
      },
    },
  },
  components: {
    ChevronRightIcon,
    ArrowBracketIcon,
    AffiliatesStep,
    ContentsStep,
    FinishStep,
    FunnelStep,
    GatewaysStep,
    InfoStep,
    PricingStep,
    BumpsStep,
    CheckoutStep,
    FullfillmentStep,
    ProofStep,
    TrackingStep,
    ModernCheckoutLinkModal,
  },
  data() {
    return {
      facebook_coversion_list: [],
      initialPricePoints: [],
      errorTabs: [],
      wizardVisitedTabs: ['info-step'],
      wizardSavedTabs: [],
      wizardDirtyTabs: [],
      orderPersistDirtySnapshot: null,
      showAllValidationErrors: false,
      validationNoticeTab: '',
      validationNoticeMessage: '',
      pendingAdvance: false,
      funnels: null,
      postValidationTab: 'pricing-step',
      currentTab: 'info-step',
      edit_product: false,
      funnel: null,
      product: null,
      pricePoint: null,
      price_points: [],
      is_main_product: 0,
      gatewayCheckBox: '',
      model: {
        commission_after_processing: false,
        primary_color: '',
        secondary_color: '',
        name: '',
        internal_name: '',
        description: '',
        funnel_id: '',
        type: 1,
        refund_period: 0,
        is_support_enabled: 0,
        support_info_type: 1,
        support_info_value: null,
        waiting_period: 0,
        sales_page: '',
        product_customization_details: null,
        password_options: null,
        disable_branding_bar: 0,
        jv_page: '',
        tracking_link: null,
        minimum_quantity: 2,
        maximum_quantity: 2,
        paypal_risk_percentage: 100,
        paypal_processing_accounts: [],
        credit_card_processing_accounts: [],
        pricing_points: [],
        checkout_page_options: {
          type: 1,
          look_and_feel: {
            template_type: 1,
          },
          form_fields: [
            { order: 1, type: 'fname', label: this.$t('frontend_first_name'), enabled: 1 },
            { order: 2, type: 'lname', label: this.$t('frontend_last_name'), enabled: 1 },
            { order: 3, type: 'email', label: 'Email', enabled: 1 },
            {
              order: 4,
              type: 'countryAndState',
              label: this.$t('frontend_product_create_country_state'),
              enabled: 1,
            },
            { order: 5, type: 'address', label: this.$t('frontend_Address'), enabled: 1 },
            {
              order: 6,
              type: 'cityAndPostalCode',
              label: this.$t('frontend_product_create_city_postalcode'),
              enabled: 1,
            },
            { order: 7, type: 'phone', label: this.$t('frontend_phone'), enabled: 1 },
            { order: 8, type: 'company', label: this.$t('frontend_company'), enabled: 1 },
          ],
          express_checkout: 0,
          cart_abandonment: {
            enabled: 0,
            options: [],
          },
          language: {
            type: 1,
            languages: [defaultLanguage],
          },
          rosca_required: 0,
          legal: {
            enabled: 0,
            html: '',
          },
          tracking_codes: '',
          exit_popup: {
            enabled: 0,
            html: '',
          },
          testimonials: 0,
          advanced_settings: {
            allowed_countries: {
              enabled: false,
              countries: [],
            },
            regional_delivery_fees: {
              enabled: false,
              default_fee_cents: 0,
              regions: [],
            },
          },
        },
        affiliate_registration_integrations: {},
        affiliates_enabled: 0,
        affiliate_landing_pages: [],
        is_main: 0,
        is_wizard_complete: 0,
      },
      assigned_to: null,
      mainWizardIndex: 0,
      gatewayIntegrations: [],
      affiliates: [],
      changeTabsAfterValidation: 1,
      saveTabsAfterValidation: 1,
      funnelProducts: null,
      isPreviouslyCompletedProduct: 0,
      showCheckoutLinksShelf: false,
      checkoutLinksLoading: false,
      checkoutLinks: [],
      showHiddenCheckoutLinks: false,
      showCheckoutLinkModal: false,
      activeCheckoutLink: null,
      isSaving: false,
      loadError: '',
    };
  },

  computed: {
    /**
     * Checkout links follow the pricing order so the shelf matches the Pricing
     * step, with links whose price points are all hidden grouped at the end.
     */
    orderedCheckoutShelfLinks() {
      return groupCheckoutLinksByPricePoints(this.checkoutLinks, this.model.pricing_points || []);
    },

    checkoutShelfRows() {
      const { primary, hidden } = this.orderedCheckoutShelfLinks;
      const rows = primary.map((link) => ({ type: 'link', key: `link-${link.id}`, link }));

      if (hidden.length) {
        rows.push({ type: 'divider', key: 'hidden-divider', count: hidden.length });
        if (this.showHiddenCheckoutLinks) {
          hidden.forEach((link) => rows.push({ type: 'link', key: `hidden-link-${link.id}`, link }));
        }
      }

      return rows;
    },

    tabs() {
      const tabTitles = [
        'frontend_products_create_tabs_info',
        'frontend_products_create_tabs_pricing',
        'frontend_products_create_tabs_gateways',
        'frontend_products_create_tabs_contents',
        'frontend_products_create_tabs_checkout',
        'frontend_products_create_tabs_bumps',
        'frontend_products_create_tabs_funnel',
        'frontend_products_create_tabs_tracking',
        'frontend_products_create_tabs_fullfillment',
        'frontend_products_create_tabs_affiliates',
        'frontend_products_create_tabs_proof',
        'frontend_products_create_tabs_finish',
      ]
      const tabs = []
      tabTitles.forEach((title, index) => {
        let t = `<span class="text-sm font-medium">${index + 1}. ${this.$t(title)}`
        if (index !== tabTitles.length - 1) {
          t += '<span class="ml-1 mr-2 text-xs gs-product-wizard__breadcrumb-chevron" aria-hidden="true">›</span>'
        }
        t += '</span>'
        /** tabName what after last _ in title */
        const tabName = title.split('_').pop() + '-step'
        tabs.push({
          title: t,
          label: this.wizardStepLabels[tabName] || this.$t(title),
          value: tabName,
        })
      })
      return tabs
    },

    wizardStepLabels() {
      return {
        'info-step': 'Information',
        'pricing-step': 'Pricing',
        'gateways-step': 'Get paid',
        'contents-step': 'Contents',
        'checkout-step': 'Checkout',
        'bumps-step': 'Order Bumps',
        'funnel-step': 'Funnel',
        'tracking-step': 'Tracking',
        'fullfillment-step': 'Fulfillment',
        'affiliates-step': 'Affiliates',
        'proof-step': 'Proof widget',
        'finish-step': 'Finish',
      };
    },

    wizardStepDescriptions() {
      return {
        'info-step': 'Set up main information about the product',
        'pricing-step': 'Pricing plans determine how customers will get billed for this product',
        'gateways-step': 'Configure payment gateways here',
        'contents-step': 'You can add the contents of your product here',
        'checkout-step': 'Customize checkout page settings for this product',
        'bumps-step': 'Set up order bump offers for this product',
        'funnel-step': 'Configure what happens after purchase',
        'tracking-step': 'Manage checkout tracking pixels, conversion code, and API events',
        'fullfillment-step': 'Set up delivery and fulfillment actions',
        'affiliates-step': 'Configure affiliate options for this product',
        'proof-step': 'Set up proof widgets and tracking details',
        'finish-step': 'Review launch details and copy anything needed before you exit',
      };
    },

    wizardStepHeadings() {
      return {
        'info-step': 'Information',
        'pricing-step': 'How do you want to price your product?',
        'gateways-step': 'How will customers pay you?',
        'contents-step': 'Contents',
        'checkout-step': 'Checkout',
        'bumps-step': 'Order Bumps',
        'funnel-step': 'Funnel',
        'tracking-step': 'Tracking',
        'fullfillment-step': 'Fulfillment',
        'affiliates-step': 'Affiliates',
        'proof-step': 'Proof widget',
        'finish-step': 'Finish',
      };
    },

    wizardNavGroups() {
      return [
        {
          title: 'Product Setup',
          steps: this.tabs.slice(0, 5),
        },
        {
          title: 'Marketing',
          steps: this.tabs.slice(5, 11),
        },
        {
          title: 'Links',
          steps: this.tabs.slice(11),
        },
      ];
    },

    currentTabIndex() {
      return this.tabs.findIndex((tab) => tab.value === this.currentTab);
    },

    currentWizardStep() {
      const label = this.wizardStepLabels[this.currentTab] || '';
      return {
        heading: this.wizardStepHeadings[this.currentTab] || label,
        description: this.wizardStepDescriptions[this.currentTab] || '',
      };
    },

    isDraftWizardMode() {
      return !this.isPreviouslyCompletedProduct;
    },

    isCurrentStepDirty() {
      return this.wizardDirtyTabs.includes(this.currentTab);
    },

    currentStepSaveStatus() {
      if (this.isSaving) {
        return 'Saving changes…';
      }

      if (this.isCurrentStepDirty) {
        return 'Unsaved changes';
      }

      if (this.wizardSavedTabs.includes(this.currentTab)) {
        return 'Changes saved';
      }

      return 'Ready to save';
    },

    paypalIntegrations() {
      return this.gatewayIntegrations.filter((integration) => {
        return integration.type == 1 && integration.platform == 'paypal';
      });
    },

    cardIntegrations() {
      return this.gatewayIntegrations.filter((integration) => {
        return integration.type == 1 && integration.platform != 'paypal';
      });
    },

    salesPage() {
      return this.model.sales_page;
    },

    paypalAddedAccounts() {
      return this.model.paypal_processing_accounts.map((a) => a.id);
    },

    cardAddedAccounts() {
      return this.model.credit_card_processing_accounts.map((a) => a.id);
    },

    mainTabClass() {
      return "pb-4 gs-default-tabs-item"
    },

    resourcesPath() {
      return `${window.gdEnv.VUE_APP_STORAGE_URL}/plugins/`;
    },

    serverPath() {
      const digitalAppTitle = this.whitelabelAppData('digital')?.title.toLowerCase() || 'groovedigital';
      return `${window.gdEnv.VUE_APP_API_URL}/${digitalAppTitle}/plugins/call/`;
    },

    pluginSiteData() {
      return this.$store.getters.pluginSiteSettings;
    },

    availableIntegrations() {
      const user = auth.authenticatedUser();
      if (user.metadata && user.metadata.integrations) {
        const integrations = [];
        user.metadata.integrations.map((value, key) => {
          const integrationType = value.type.split('-')[0];
          const integrationPlugin = `${integrationType}-component`;
          if (integrations.findIndex((x) => x.value == integrationPlugin) == -1) {
            integrations.push({
              text: integrationType.charAt(0).toUpperCase() + integrationType.slice(1),
              value: integrationPlugin,
            });
          }
        });

        return integrations;
      }
      return [];
    },
  },

  watch: {
    salesPage(newVal) {
      if (
        this.model.affiliate_landing_pages &&
        this.model.affiliate_landing_pages[0]
      ) {
        this.model.affiliate_landing_pages[0].landing_page = newVal;
      }
    },
  },

  async created() {
    this.$wait.start('create_product');
    try {
    this.funnel = await Funnel.$find(this.$route.params.id);



    const FbSettings = await axios.post('/conversion-settings', {
      list: true,
    });

    this.facebook_coversion_list = FbSettings.data.data;

    if (this.$route.params.product_id == undefined) {
      this.product = new Product({}).for(this.funnel);

      if (
        this.$route.query.product_type &&
        this.$route.query.product_type !== undefined
      ) {
        
        this.model.type = parseInt(this.$route.query.product_type);
      }
    } else {
      this.product = await this.funnel
        .products()
        .$find(this.$route.params.product_id);


      this.model.commission_after_processing = this.product.commission_after_processing;
      this.model.funnel_id = this.product.funnel_id;
      this.model.name = this.product.name;
      this.model.internal_name = this.product.internal_name;
      this.model.description = this.product.description;
      this.model.type = this.product.type;
      this.model.refund_period = this.product.refund_period;
      this.model.is_support_enabled = this.product.is_support_enabled;
      this.model.support_info_type = this.product.support_info_type;
      this.model.product_customization_details = this.product.product_customization_details;
      this.model.password_options = this.product.password_options;
      this.model.disable_branding_bar = this.product.disable_branding_bar;
      this.model.support_info_value = this.product.support_info_value;
      this.model.waiting_period = this.product.waiting_period;
      this.model.tracking_link = this.product.tracking_link;
      this.model.sales_page = this.product.sales_page;
      this.model.jv_page = this.product.jv_page;
      this.model.checkout_page_options = this.product.checkout_page_options;

      this.model.primary_color = '#d9237f';
      this.model.secondary_color = '#ffffff';

      if (
        this.product.checkout_page_options.look_and_feel.template_type !=
        undefined &&
        this.product.checkout_page_options.look_and_feel.template_type == 4
      ) {
        this.model.primary_color = this.product.checkout_page_options
          .look_and_feel.custom_color.primary_color
          ? this.product.checkout_page_options.look_and_feel.custom_color
            .primary_color
          : '#d9237f';
        this.model.secondary_color = this.product.checkout_page_options
          .look_and_feel.custom_color.secondary_color
          ? this.product.checkout_page_options.look_and_feel.custom_color
            .secondary_color
          : '#ffffff';
      }

      this.model.affiliate_registration_integrations =
        this.product.affiliate_registration_integrations;
      this.model.affiliate_data_share_details =
        this.product.affiliate_data_share_details;

      this.model.paypal_risk_percentage = this.product.paypal_risk_percentage;

      if (this.product.paypal_processing_accounts) {
        this.model.paypal_processing_accounts =
          this.product.paypal_processing_accounts;
      }

      if (this.product.credit_card_processing_accounts) {
        this.model.credit_card_processing_accounts =
          this.product.credit_card_processing_accounts;
      }

      if (this.product.paypal_processing_accounts) {
        this.gatewayCheckBox = 'paypal';
      } else if (this.product.credit_card_processing_accounts) {
        this.gatewayCheckBox = 'card';
      }

      const productPricingPoints = await this.product.pricepoints().$get();


      for (let i = 0; i < productPricingPoints.length; i++) {
        const singlePricingPoint = productPricingPoints[i];
        this.price_points.push(singlePricingPoint);

        let pixelId = singlePricingPoint.pixel_id;
        if (!singlePricingPoint.facebook_conversion_api_enabled) {
          pixelId = 0;
        }


        this.model.pricing_points.push({
          id: singlePricingPoint.id,
          type: singlePricingPoint.type,
          name: singlePricingPoint.name,
          internal_name: singlePricingPoint.internal_name,
          description: singlePricingPoint.description,
          price: singlePricingPoint.price,
          recurring_interval: singlePricingPoint.recurring_interval,
          recurrence_interval: singlePricingPoint.recurrence_interval,
          number_of_installments: singlePricingPoint.number_of_installments,
          coupons_enabled: singlePricingPoint.coupons_enabled,
          is_trial: singlePricingPoint.is_trial,
          trial_price: singlePricingPoint.trial_price,
          trial_period: singlePricingPoint.trial_period,
          trial_period_type: singlePricingPoint.trial_period_type,
          salespage_type: singlePricingPoint.salespage_type,
          salespage_details: singlePricingPoint.salespage_details,
          maximum_quantity: singlePricingPoint.maximum_quantity,
          minimum_quantity: singlePricingPoint.minimum_quantity,
          quantity_enable: singlePricingPoint.quantity_enable,
          offer_bump_enable: singlePricingPoint.offer_bump_enable,
          offer_bump_options: singlePricingPoint.offer_bump_options,
          post_sale_order_bump_options:
            singlePricingPoint.post_sale_order_bump_options,
          post_checkout_type: singlePricingPoint.post_checkout_type,
          post_checkout_details: singlePricingPoint.post_checkout_details,
          post_transaction_details: singlePricingPoint.post_transaction_details,
          post_sale_notification_type:
            singlePricingPoint.post_sale_notification_type,
          post_sale_details: singlePricingPoint.post_sale_details,
          post_sale_integrations: singlePricingPoint.post_sale_integrations,
          memberships: singlePricingPoint.memberships,
          file_memberships: singlePricingPoint.file_memberships,
          legal_options: singlePricingPoint.legal_options,
          abandon_cart_integrations:
            singlePricingPoint.abandon_cart_integrations,
          affiliate_registration_integrations:
            singlePricingPoint.affiliate_registration_integrations,
          customer_portal_details: singlePricingPoint.customer_portal_details,
          content_access_type: singlePricingPoint.content_access_type,
          content_access_options: singlePricingPoint.content_access_options,
          advanced_tracking_code_enabled:
            singlePricingPoint.advanced_tracking_code_enabled,

          facebook_conversion_api_enabled:
            singlePricingPoint.facebook_conversion_api_enabled,

          pixel_id: pixelId,

          advanced_tracking_code_details:
            singlePricingPoint.advanced_tracking_code_details,
          pricing_string: singlePricingPoint.pricing_string,
          currency_symbol: singlePricingPoint.currency_symbol,
          currency_code: singlePricingPoint.currency_code,
          commission_type: singlePricingPoint.commission_type,
          commission_details: singlePricingPoint.commission_details,
          is_default: singlePricingPoint.is_default,
          is_pinned: normalizePricePoint(singlePricingPoint).is_pinned,
          pin_priority: normalizePricePoint(singlePricingPoint).pin_priority,
          sort_order: normalizePricePoint(singlePricingPoint).sort_order,
        });
      }

      this.model.affiliates_enabled = this.product.affiliates_enabled;

      if (
        this.product.affiliate_landing_pages &&
        this.product.affiliate_landing_pages.length > 0
      ) {
        this.model.affiliate_landing_pages =
          this.product.affiliate_landing_pages;
      }

      this.model.is_main = this.product.is_main;
      this.model.is_wizard_complete = this.product.is_wizard_complete;
      // this.affiliates = await this.funnel.affiliates().$get();
      this.edit_product = true;

      this.isPreviouslyCompletedProduct = this.product.is_wizard_complete;
    }

    this.gatewayIntegrations = await Integration.$get();
    const funnelProducts = await this.funnel.products().$get();

    const tempFunnel = [];
    funnelProducts.find((product) => {
      if (product.id != this.$route.params.product_id) {
        tempFunnel.push(product);
      }
    });

    for (let j = 0; j < this.model.pricing_points.length; j++) {
      this.model.pricing_points[j].funnelProducts = tempFunnel;
      if (this.model.pricing_points[j].offer_bump_enable) {
      } else {
        this.model.pricing_points[j].productPricePoint = null;
        this.model.pricing_points[j].offer_bump_options.product_id = 0;
        this.model.pricing_points[j].offer_bump_options.price_point_id = 0;
      }

      if (
        this.model.pricing_points[j].post_sale_order_bump_options
          .post_order_bump_enable &&
        this.model.pricing_points[j].post_sale_order_bump_options
          .post_order_product_id != null &&
        this.model.pricing_points[j].post_sale_order_bump_options
          .post_order_product_id > 0
      ) {

        const pro = tempFunnel.find(
          (product) =>
            product.id ==
            this.model.pricing_points[j].post_sale_order_bump_options
              .post_order_product_id
        );
        this.model.pricing_points[j].postSaleProductPricePoint = pro?.price_points;
      } else {
        this.model.pricing_points[j].postSaleProductPricePoint = null;
        this.model.pricing_points[
          j
        ].post_sale_order_bump_options.post_order_product_id = 0;
        this.model.pricing_points[
          j
        ].post_sale_order_bump_options.post_order_price_point_id = 0;
      }
    }

    const shouldShowValidation = this.$route.query.validate && this.$route.query.validate == 1;
    await this.validateAllTabs(Boolean(shouldShowValidation));
    this.initializeWizardProgress();
    if (shouldShowValidation && this.errorTabs.length) {
      this.currentTab = this.errorTabs[0];
      this.markWizardStepVisited(this.currentTab);
    }
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    this.$nextTick(() => {
      this.wizardDirtyTabs = [];
    });

    this.initialPricePoints = [...this.price_points];
    } catch (error) {
      this.loadError = this.getProductErrorMessage(
        error,
        'GrooveSell could not load this product. Please try again.'
      );
      console.error('Unable to load GrooveSell product wizard', error);
    } finally {
      this.$wait.end('create_product');
    }
  },

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  },

  methods: {
    sendToFunnelProducts() {
      if (this.funnel && this.funnel.id) {
        this.$router.push({ name: 'products', params: { id: this.funnel.id } });
        return;
      }

      this.$router.push({ name: 'funnels' });
    },

    retryProductWizard() {
      window.location.reload();
    },

    getProductErrorMessage(error, fallback) {
      const responseMessage = error && error.response && error.response.data
        ? error.response.data.message || error.response.data.error
        : '';
      const normalizedMessage = String(responseMessage || '').toLowerCase();

      if (
        (error && error.response && [401, 403].includes(error.response.status)) ||
        normalizedMessage.includes('unauthorized') ||
        normalizedMessage.includes('not authorized')
      ) {
        return 'Your account does not have access to this funnel or product. Return to your funnels and choose a product you own.';
      }

      return responseMessage || fallback;
    },

    endProductSaveLoaders() {
      [
        'saveBasicProductInfo',
        'saveAllInfo',
        'saveProductInfo',
        'savePricePointsInfo',
      ].forEach((loaderName) => this.$loader.end(loaderName));
    },

    async persistProductChanges(saveAction, successMessage = 'Product changes saved.') {
      if (this.isSaving) {
        return false;
      }

      this.isSaving = true;

      try {
        await saveAction();
        this.$gsToast.success(successMessage);
        return true;
      } catch (error) {
        console.error('Unable to save GrooveSell product', error);
        this.$gsToast.error(
          this.getProductErrorMessage(
            error,
            'GrooveSell could not finish saving. Your changes are still on screen—please try again.'
          )
        );
        return false;
      } finally {
        this.endProductSaveLoaders();
        this.isSaving = false;
      }
    },

    async saveProductFromChild() {
      const saved = await this.persistProductChanges(() => this.saveProductInfo());

      if (saved) {
        this.markWizardStepSaved(this.currentTab);
      }

      return saved;
    },

    async openCheckoutLinksShelf() {
      if (!this.product || !this.product.id) {
        return;
      }

      this.showCheckoutLinksShelf = true;
      this.checkoutLinksLoading = true;

      try {
        const selectedProduct = await Product.$find(this.product.id);
        this.checkoutLinks = await selectedProduct.checkoutLinks().$get();
      } finally {
        this.checkoutLinksLoading = false;
      }
    },

    openCheckoutLinksManager() {
      if (!this.product || !this.product.id) {
        return;
      }

      this.$router.push({
        name: 'productsCheckoutLinks',
        params: {
          id: this.funnel.id,
          product_id: this.product.id,
        },
      });
    },

    openHostedCheckout(checkoutLink) {
      if (checkoutLink.checkout_link) {
        window.open(checkoutLink.checkout_link, '_blank');
      }
    },

    checkoutLinkPricePoints(checkoutLink) {
      return this.checkoutLinkMatchedPricePoints(checkoutLink).slice(0, 3);
    },

    checkoutLinkMatchedPricePoints(checkoutLink) {
      return pricePointsForCheckoutLink(this.model.pricing_points || [], checkoutLink);
    },

    checkoutPricePointSummary(checkoutLink) {
      const points = this.checkoutLinkPricePoints(checkoutLink);
      if (!points.length) return 'Price-point details available in the manager.';
      const labels = points.map((point) => `${point.name || 'Price point'}${point.price != null ? ` — $${point.price}` : ''}`);
      return labels.join(' • ');
    },

    openCheckoutLinkModal(checkoutLink) {
      this.activeCheckoutLink = checkoutLink;
      this.showCheckoutLinkModal = true;
    },

    copyCheckoutLink(checkoutLink) {
      if (!checkoutLink.checkout_link || !navigator.clipboard) {
        return;
      }

      navigator.clipboard.writeText(checkoutLink.checkout_link);
    },

    isWizardStepComplete(stepValue) {
      if (!this.tabs.some((tab) => tab.value === stepValue) || this.isWizardStepError(stepValue)) {
        return false;
      }

      return this.wizardSavedTabs.includes(stepValue);
    },

    isWizardStepError(stepValue) {
      if (!this.errorTabs.includes(stepValue)) {
        return false;
      }

      return this.showAllValidationErrors || this.wizardVisitedTabs.includes(stepValue);
    },

    markWizardStepVisited(stepValue) {
      if (!this.wizardVisitedTabs.includes(stepValue)) {
        this.wizardVisitedTabs.push(stepValue);
      }
    },

    markWizardStepSaved(stepValue) {
      if (!this.wizardSavedTabs.includes(stepValue)) {
        this.wizardSavedTabs.push(stepValue);
      }
      this.wizardDirtyTabs = this.wizardDirtyTabs.filter((tab) => tab !== stepValue);
    },

    markWizardStepDirty(stepValue) {
      if (!this.wizardDirtyTabs.includes(stepValue)) {
        this.wizardDirtyTabs.push(stepValue);
      }
      this.wizardSavedTabs = this.wizardSavedTabs.filter((tab) => tab !== stepValue);
    },

    handleModelUpdate(model) {
      this.model = model;
      this.markWizardStepDirty(this.currentTab);
    },

    handlePricePointsUpdate(pricePoints) {
      this.price_points = pricePoints;
      this.markWizardStepDirty(this.currentTab);
    },

    beginOrderPersist() {
      this.orderPersistDirtySnapshot = {
        tab: this.currentTab,
        wasDirty: this.wizardDirtyTabs.includes(this.currentTab),
        wasSaved: this.wizardSavedTabs.includes(this.currentTab),
      };
    },

    endOrderPersist() {
      const snap = this.orderPersistDirtySnapshot;
      this.orderPersistDirtySnapshot = null;

      if (!snap || snap.wasDirty) {
        return;
      }

      this.$nextTick(() => {
        this.wizardDirtyTabs = this.wizardDirtyTabs.filter(tab => tab !== snap.tab);

        if (snap.wasSaved && !this.wizardSavedTabs.includes(snap.tab)) {
          this.wizardSavedTabs.push(snap.tab);
        }
      });
    },

    handleBeforeUnload(event) {
      if (!this.wizardDirtyTabs.length) {
        return;
      }

      event.preventDefault();
      event.returnValue = '';
    },

    wizardGroupCompletedCount(group) {
      return group.steps.filter((step) => this.isWizardStepComplete(step.value)).length;
    },

    isWizardGroupComplete(group) {
      return group.steps.length > 0 && this.wizardGroupCompletedCount(group) === group.steps.length;
    },

    wizardGroupHasError(group) {
      return group.steps.some((step) => this.isWizardStepError(step.value));
    },

    wizardGroupProgressLabel(group) {
      if (this.isWizardGroupComplete(group)) {
        return `${group.title} complete`;
      }

      if (this.wizardGroupHasError(group)) {
        return `${group.title} has items that need attention`;
      }

      return `${this.wizardGroupCompletedCount(group)} of ${group.steps.length} steps complete`;
    },

    hasPersistedWizardStepData(stepValue) {
      const pricePoints = this.model.pricing_points || [];

      if (stepValue === 'info-step') {
        return Boolean(this.model.name && this.model.internal_name && this.model.description);
      }

      if (stepValue === 'pricing-step') {
        return pricePoints.length > 0 && pricePoints.every((pricePoint) => {
          return Boolean(pricePoint.name && pricePoint.internal_name && pricePoint.description)
            && pricePoint.price !== '' && pricePoint.price !== null && pricePoint.price !== undefined;
        });
      }

      if (stepValue === 'gateways-step') {
        return Boolean(
          (this.model.credit_card_processing_accounts || []).length
          || (this.model.paypal_processing_accounts || []).length
        );
      }

      if (stepValue === 'contents-step') {
        return Boolean(this.model.sales_page)
          && this.model.refund_period !== ''
          && this.model.waiting_period !== ''
          && !this.errorTabs.includes(stepValue);
      }

      if (stepValue === 'checkout-step') {
        const checkoutOptions = this.model.checkout_page_options || {};
        return Boolean(
          this.edit_product
          && checkoutOptions.look_and_feel
          && (checkoutOptions.form_fields || []).length
        );
      }

      if (stepValue === 'bumps-step') {
        return pricePoints.some((pricePoint) => {
          return Boolean(
            pricePoint.offer_bump_enable
            || pricePoint.post_sale_order_bump_options?.post_order_bump_enable
          );
        });
      }

      if (stepValue === 'funnel-step') {
        return pricePoints.some((pricePoint) => {
          const details = pricePoint.post_transaction_details || {};
          return Boolean(details.post_transaction_url || pricePoint.post_transaction_type);
        });
      }

      if (stepValue === 'tracking-step') {
        return Boolean(this.model.checkout_page_options?.tracking_codes)
          || pricePoints.some((pricePoint) => {
            return Boolean(
              pricePoint.advanced_tracking_code_enabled
              || pricePoint.facebook_conversion_api_enabled
            );
          });
      }

      if (stepValue === 'fullfillment-step') {
        return pricePoints.some((pricePoint) => {
          return Boolean(
            (pricePoint.memberships || []).length
            || (pricePoint.post_sale_integrations || []).length
            || (pricePoint.file_memberships || []).length
            || (pricePoint.post_sale_details?.webhooks || []).length
          );
        });
      }

      if (stepValue === 'affiliates-step') {
        return Boolean(this.model.affiliates_enabled);
      }

      return stepValue === 'finish-step' && Boolean(this.model.is_wizard_complete);
    },

    initializeWizardProgress() {
      this.wizardSavedTabs = this.tabs
        .filter((step) => {
          if (this.errorTabs.includes(step.value)) {
            return false;
          }

          return Boolean(this.isPreviouslyCompletedProduct)
            || this.hasPersistedWizardStepData(step.value);
        })
        .map((step) => step.value);

      this.wizardVisitedTabs = Array.from(new Set(['info-step', ...this.wizardSavedTabs]));
    },

    async fetchIntegrations() {
      this.gatewayIntegrations = await Integration.$get();
    },
    handleTabChange(newTab) {
      if (newTab === this.currentTab || this.isSaving) {
        return;
      }

      this.currentTab = newTab;
      this.markWizardStepVisited(newTab);
    },

    moveOnWizard(next) {
      if (this.isSaving) {
        return false;
      }

      if (next) {
        return this.requestStepSave(true);
      }

      const currentlySelectedTabIndex = this.tabs
        .map((item) => item.value)
        .indexOf(this.currentTab);
      const tabIndex = currentlySelectedTabIndex - 1;

      if (tabIndex < 0) {
        return false;
      }

      this.currentTab = this.tabs[tabIndex].value;
      this.markWizardStepVisited(this.currentTab);
      return true;
    },

    requestStepSave(advance = false) {
      if (this.isSaving) {
        return false;
      }

      const currentIndex = this.currentTabIndex;
      this.pendingAdvance = advance && currentIndex < this.tabs.length - 1;
      this.postValidationTab = this.pendingAdvance ? this.tabs[currentIndex + 1].value : this.currentTab;
      const refToValidate = this.$refs[this.currentTab];

      if (!refToValidate || typeof refToValidate.validate !== 'function') {
        this.pendingAdvance = false;
        this.$gsToast.error('This step could not be validated. Please reload the page and try again.');
        return false;
      }

      return refToValidate.validate();
    },

    focusFirstInvalidControl() {
      this.$nextTick(() => {
        const invalidControl = this.$el.querySelector(
          '.gs-product-wizard__canvas [aria-invalid="true"], .gs-product-wizard__canvas .is-invalid input, .gs-product-wizard__canvas input:invalid'
        );

        if (invalidControl && typeof invalidControl.focus === 'function') {
          invalidControl.focus();
          invalidControl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    },

    isValidationInvalid(validationNode) {
      return Boolean(validationNode && validationNode.$invalid);
    },

    pricingPointValidationAt(index) {
      const pricingPointsValidation = this.$v.model.pricing_points;
      return pricingPointsValidation && pricingPointsValidation.$each
        ? pricingPointsValidation.$each[index] || {}
        : {};
    },

    async validateAllTabs(touch = true) {
      this.errorTabs = [];
      this.showAllValidationErrors = touch;

      if (touch) {
        this.$v.$touch();
      }

      if (this.$v.model.$invalid) {
        if (
          this.isValidationInvalid(this.$v.model.name) ||
          this.isValidationInvalid(this.$v.model.internal_name) ||
          this.isValidationInvalid(this.$v.model.description)
        ) {
          this.errorTabs.push('info-step');
        }

        for (let i = 0; i < this.model.pricing_points.length; i++) {
          const pricePointValidation = this.pricingPointValidationAt(i);
          if (
            this.isValidationInvalid(pricePointValidation.name) ||
            this.isValidationInvalid(pricePointValidation.internal_name) ||
            this.isValidationInvalid(pricePointValidation.description) ||
            this.isValidationInvalid(pricePointValidation.price) ||
            this.isValidationInvalid(pricePointValidation.number_of_installments) ||
            this.isValidationInvalid(pricePointValidation.trial_price) ||
            this.isValidationInvalid(pricePointValidation.trial_period)
          ) {
            if (!this.errorTabs.includes('pricing-step')) {
              this.errorTabs.push('pricing-step');
            }
          }
        }

        for (let i = 0; i < this.model.paypal_processing_accounts.length; i++) {
          const paypalValidation = this.$v.model.paypal_processing_accounts;
          if (
            paypalValidation &&
            paypalValidation.$each &&
            this.isValidationInvalid(paypalValidation.$each[i])
          ) {
            if (!this.errorTabs.includes('gateways-step')) {
              this.errorTabs.push('gateways-step');
            }
          }
        }

        if (!this.errorTabs.includes('gateways-step')) {
          for (
            let i = 0;
            i < this.model.credit_card_processing_accounts.length;
            i++
          ) {
            const cardValidation = this.$v.model.credit_card_processing_accounts;
            if (
              cardValidation &&
              cardValidation.$each &&
              this.isValidationInvalid(cardValidation.$each[i])
            ) {
              if (!this.errorTabs.includes('gateways-step')) {
                this.errorTabs.push('gateways-step');
              }
            }
          }
        }

        if (
          this.isValidationInvalid(this.$v.model.sales_page) ||
          this.isValidationInvalid(this.$v.model.refund_period) ||
          this.isValidationInvalid(this.$v.model.waiting_period) ||
          this.isValidationInvalid(this.$v.model.support_info_value)
        ) {
          this.errorTabs.push('contents-step');
        }

        if (!this.errorTabs.includes('contents-step')) {
          for (let i = 0; i < this.model.pricing_points.length; i++) {
            const pricePointValidation = this.pricingPointValidationAt(i);
            if (
              this.isValidationInvalid(pricePointValidation.post_checkout_details)
            ) {
              if (!this.errorTabs.includes('contents-step')) {
                this.errorTabs.push('contents-step');
              }
            }
          }
        }

        for (let i = 0; i < this.model.pricing_points.length; i++) {
          const pricePointValidation = this.pricingPointValidationAt(i);
          if (
            this.isValidationInvalid(pricePointValidation.offer_bump_options)
          ) {
            if (!this.errorTabs.includes('bumps-step')) {
              this.errorTabs.push('bumps-step');
            }
          }
          if (
            this.isValidationInvalid(
              pricePointValidation.post_sale_order_bump_options
            )
          ) {
            if (!this.errorTabs.includes('bumps-step')) {
              this.errorTabs.push('bumps-step');
            }
          }
        }

        for (let i = 0; i < this.model.pricing_points.length; i++) {
          const pricePointValidation = this.pricingPointValidationAt(i);
          if (
            this.isValidationInvalid(pricePointValidation.post_transaction_details)
          ) {
            if (!this.errorTabs.includes('funnel-step')) {
              this.errorTabs.push('funnel-step');
            }
          }
        }

        for (let i = 0; i < this.model.pricing_points.length; i++) {
          const pricePointValidation = this.pricingPointValidationAt(i);
          if (
            this.isValidationInvalid(pricePointValidation.post_sale_details)
          ) {
            if (!this.errorTabs.includes('fullfillment-step')) {
              this.errorTabs.push('fullfillment-step');
            }
          }
        }

        if (this.isValidationInvalid(this.$v.model.jv_page)) {
          this.errorTabs.push('affiliates-step');
        }

        if (!this.errorTabs.includes('affiliates-step')) {
          for (let i = 0; i < this.model.pricing_points.length; i++) {
            const pricePointValidation = this.pricingPointValidationAt(i);
            if (
              this.isValidationInvalid(pricePointValidation.commission_details)
            ) {
              if (!this.errorTabs.includes('affiliates-step')) {
                this.errorTabs.push('affiliates-step');
              }
            }
          }
        }

        if (!this.errorTabs.includes('affiliates-step')) {
          for (let i = 0; i < this.model.pricing_points.length; i++) {
            const pricePointValidation = this.pricingPointValidationAt(i);
            const commissionValidation = pricePointValidation.commission_details;
            for (
              let j = 0;
              j < this.model.pricing_points[i].commission_details.length;
              j++
            ) {
              if (
                commissionValidation &&
                commissionValidation.$each &&
                this.isValidationInvalid(commissionValidation.$each[j]?.name)
              ) {
                if (!this.errorTabs.includes('affiliates-step')) {
                  this.errorTabs.push('affiliates-step');
                }
              }
            }
          }
        }

        if (!this.errorTabs.includes('affiliates-step')) {
          for (let i = 0; i < this.model.affiliate_landing_pages.length; i++) {
            const landingPagesValidation = this.$v.model.affiliate_landing_pages;
            if (
              landingPagesValidation &&
              landingPagesValidation.$each &&
              this.isValidationInvalid(landingPagesValidation.$each[i])
            ) {
              if (!this.errorTabs.includes('affiliates-step')) {
                this.errorTabs.push('affiliates-step');
              }
            }
          }
        }
        return false;
      }
      return true;
    },

    async exitWizardForCompletedProducts(justSave = false) {
      const validationFlag = await this.validateAllTabs();

      if (!validationFlag) {
        this.showFirstWizardError();
        return;
      }

      const saved = await this.persistProductChanges(
        () => this.saveAllInfo(),
        justSave ? 'Product changes saved.' : 'Product saved. Returning to your products…'
      );

      if (saved) {
        this.wizardDirtyTabs = [];
      }

      if (saved && !justSave) {
        this.$router.push({
          name: 'products',
          params: { id: this.funnel.id },
        });
      }
    },

    async saveAndExitWizard() {
      const validationFlag = await this.validateAllTabs();

      if (!validationFlag) {
        this.showFirstWizardError();
        return;
      }

      const saved = await this.persistProductChanges(
        () => this.saveAllInfo(),
        'Product saved. Returning to your products…'
      );

      if (saved) {
        this.wizardDirtyTabs = [];
        this.$router.push({ name: 'products', params: { id: this.funnel.id } });
      }
    },

    showFirstWizardError() {
      if (!this.errorTabs.length) {
        this.$gsToast.error('Review the highlighted product fields before saving.');
        return;
      }

      this.currentTab = this.errorTabs[0];
      this.markWizardStepVisited(this.currentTab);
      this.validationNoticeTab = this.currentTab;
      this.validationNoticeMessage = 'This product cannot be finished yet. Review the highlighted fields on this step, save it, and then continue.';
      this.focusFirstInvalidControl();
    },

    async saveBasicProductInfo() {

      this.$loader.start('saveBasicProductInfo');
      this.product.funnel_id = this.funnel.id;
      this.product.name = this.model.name;
      this.product.commission_after_processing =
        this.model.commission_after_processing;
      this.product.internal_name = this.model.internal_name;
      this.product.description = this.model.description;
      this.product.type = this.model.type;
      this.product.refund_period = this.model.refund_period;
      this.product.product_customization_details =
        this.model.product_customization_details;
      this.product.password_options = this.model.password_options;
      this.product.disable_branding_bar = this.model.disable_branding_bar;
      this.product.is_support_enabled = this.model.is_support_enabled;
      this.product.support_info_type = this.model.support_info_type;
      this.product.support_info_value = this.model.support_info_value;
      this.product.waiting_period = this.model.waiting_period;
      this.product.sales_page = this.model.sales_page;
      this.product.jv_page = this.model.jv_page;
      this.product.credit_card_processing_accounts =
        this.model.credit_card_processing_accounts;
      this.product.paypal_risk_percentage = this.model.paypal_risk_percentage;
      this.product.paypal_processing_accounts =
        this.model.paypal_processing_accounts;
      this.product.affiliate_landing_pages = this.model.affiliate_landing_pages;
      this.product.affiliate_registration_integrations =
        this.model.affiliate_registration_integrations;
      this.product.checkout_page_options = this.model.checkout_page_options;
      this.product.affiliates_enabled = this.model.affiliates_enabled;
      this.product.is_wizard_complete = this.model.is_wizard_complete;
      this.product.affiliate_data_share_details =
        this.model.affiliate_data_share_details;
      const savedProductData = await this.product.save();

      if (!this.edit_product) {
        this.product = await this.funnel
          .products()
          .$find(savedProductData.data.id);
      }

      this.model.funnel_id = this.product.funnel_id;
      this.model.name = this.product.name;
      this.model.internal_name = this.product.internal_name;
      this.model.description = this.product.description;
      this.model.type = this.product.type;
      this.model.refund_period = this.product.refund_period;
      this.model.is_support_enabled = this.product.is_support_enabled;
      this.model.support_info_type = this.product.support_info_type;
      this.model.support_info_value = this.product.support_info_value;
      this.model.waiting_period = this.product.waiting_period;
      this.model.tracking_link = this.product.tracking_link;
      this.model.sales_page = this.product.sales_page;
      this.model.jv_page = this.product.jv_page;
      this.model.product_customization_details =
        this.product.product_customization_details;
      this.model.password_options = this.product.password_options;
      this.model.disable_branding_bar = this.product.disable_branding_bar;
      this.model.affiliate_data_share_details =
        this.product.affiliate_data_share_details;

      if (!this.edit_product || this.price_points.length == 0) {
        const funnelProducts = await this.funnel.products().$get();
        const productPricePoints = funnelProducts[0].price_points;
        if (funnelProducts[0].price_points.length > 0) {
          var seletectPricePoint = productPricePoints[0].id;
        } else {
          var seletectPricePoint = null;
          const productPricePoints = null;
        }
        const commision_id = Math.ceil(Math.random() * 10000);

        this.pricePoint = new PricePoint({
          type: 1,
          name: `${this.product.name} #1`,
          internal_name: '',
          description: '',
          price: 1,
          recurring_interval: 1,
          number_of_installments: 1,
          recurrence_interval: 1,
          coupons_enabled:1,
          is_trial: 0,
          trial_price: 0.0,
          trial_period: 0,
          trial_period_type: 1,
          salespage_type: 1,
          salespage_details: {
            sales_page: '',
          },
          funnelProducts,
          productPricePoint: productPricePoints,
          postSaleProductPricePoint: productPricePoints,
          maximum_quantity: 5,
          minimum_quantity: 1,
          quantity_enable: 0,
          offer_bump_enable: 0,
          offer_bump_options: [],
          post_sale_order_bump_options: {
            post_order_heading_line: null,
            post_order_subheading: null,
            post_order_description: null,
            post_order_video_details: {
              post_order_show_video: 0,
              url: '',
              id: null,
              provider: '',
              permalink: null,
            },
            post_order_bump_enable: 0,
            post_order_display_thumbnail: 0,
            post_order_thumbnail_image: '',
            post_order_is_square: 0,
            post_order_thumbnail_size: 'md',
            post_order_style_id: 1,
            post_order_product_id: null,
            post_order_price_point_id: null,
          },
          post_checkout_type: 1,
          post_checkout_details: {
            thankyou_page: '',
          },
          post_transaction_details: {
            type: 1,
            post_transaction_url: '',
          },
          post_sale_notification_type: 1,
          post_sale_details: null,
          post_sale_integrations: null,
          memberships: null,
          file_memberships: null,
          legal_options: {
            enabled: 0,
            html: '',
          },
          abandon_cart_integrations: null,
          affiliate_registration_integrations: null,
          customer_portal_details: null,
          content_access_type: 0,
          content_access_options: {},
          advanced_tracking_code_enabled: 0,
          advanced_tracking_code_details: {
            tracking_script: '',
          },
          pricing_string: 'One-time payment of $1.00',
          commission_type: 1,
          commission_details: [
            {
              id: commision_id,
              type: 1,
              name: 'Default',
              assigned_to: null,
              sales_commission: {
                commission: 0,
                commission_type: 'percent',
                jv_broker_commission: 0,
                jv_broker_commission_type: 'percent',
              },
              trial_commission: {
                commission: 0,
                commission_type: 'percent',
                jv_broker_commission: 0,
                jv_broker_commission_type: 'percent',
              },
              rebill_commissions: [
                {
                  commission: 0,
                  no_of_payments: null,
                  commission_type: 'percent',
                  jv_broker_commission: 0,
                  jv_broker_commission_type: 'percent',
                },
              ],
            },
          ],
          is_default: 1,
          is_pinned: true,
          pin_priority: 0,
          sort_order: 1,
        }).for(this.product);

        const savedProductData = await this.pricePoint.save();
        this.pricePoint = await this.product
          .pricepoints()
          .$find(savedProductData.data.id);
        this.product.price_points.push(this.pricePoint);
        const postVideoDetails = {
          post_order_show_video: 0,
          url: '',
          id: null,
          provider: '',
          permalink: null,
        };
        this.model.pricing_points.push({
          id: this.pricePoint.id,
          type: 1,
          name: `${this.product.name} #1`,
          internal_name: '',
          description: '',
          price: 1,
          recurring_interval: 1,
          number_of_installments: 1,
          recurrence_interval: 1,
          coupons_enabled:1,
          is_trial: 0,
          trial_price: 0.0,
          trial_period: 0,
          trial_period_type: 1,
          salespage_type: 1,
          salespage_details: {
            sales_page: '',
          },
          funnelProducts,
          productPricePoint: productPricePoints,
          postSaleProductPricePoint: productPricePoints,
          maximum_quantity: this.pricePoint.maximum_quantity
            ? this.pricePoint.maximum_quantity
            : 1,
          minimum_quantity: this.pricePoint.minimum_quantity
            ? this.pricePoint.minimum_quantity
            : 5,
          quantity_enable: this.pricePoint.quantity_enable,
          offer_bump_enable: this.pricePoint.offer_bump_enable,
          offer_bump_options: this.pricePoint.offer_bump_options,
          post_sale_order_bump_options: {
            post_order_heading_line:
              this.pricePoint.post_sale_order_bump_options
                .post_order_heading_line,
            post_order_subheading:
              this.pricePoint.post_sale_order_bump_options
                .post_order_subheading,
            post_order_description:
              this.pricePoint.post_sale_order_bump_options
                .post_order_description,

            post_order_video_details:
              this.pricePoint.post_sale_order_bump_options.hasOwnProperty(
                'post_order_video_details'
              )
                ? this.pricePoint.post_sale_order_bump_options
                  .post_order_video_details
                : postVideoDetails,

            post_order_bump_enable:
              this.pricePoint.post_sale_order_bump_options
                .post_order_bump_enable,
            post_order_display_thumbnail:
              this.pricePoint.post_sale_order_bump_options
                .post_order_display_thumbnail,
            post_order_thumbnail_image:
              this.pricePoint.post_sale_order_bump_options
                .post_order_thumbnail_image,
            post_order_is_square:
              this.pricePoint.post_sale_order_bump_options.post_order_is_square,
            post_order_thumbnail_size:
              this.pricePoint.post_sale_order_bump_options
                .post_order_thumbnail_size,
            post_order_style_id:
              this.pricePoint.post_sale_order_bump_options.post_order_style_id,
            post_order_product_id:
              this.pricePoint.post_sale_order_bump_options
                .post_order_product_id,
            post_order_price_point_id:
              this.pricePoint.post_sale_order_bump_options
                .post_order_price_point_id,
          },
          post_checkout_type: 1,
          post_checkout_details: {
            thankyou_page: '',
          },
          post_transaction_details: {
            type: 1,
            post_transaction_url: '',
          },
          post_sale_notification_type: 1,
          post_sale_details: null,
          post_sale_integrations: null,
          memberships: null,
          file_memberships: null,
          legal_options: {
            enabled: 0,
            html: '',
          },
          abandon_cart_integrations: null,
          affiliate_registration_integrations: null,
          customer_portal_details: null,
          content_access_type: 0,
          content_access_options: {},
          advanced_tracking_code_enabled: 0,
          facebook_conversion_api_enabled: 0,
          pixel_id: 0,
          advanced_tracking_code_details: {
            tracking_script: '',
          },
          pricing_string: 'One-time payment of $1.00',
          currency_symbol: this.pricePoint.currency_symbol,
          currency_code: this.pricePoint.currency_code,
          commission_type: 1,
          commission_details: [
            {
              id: commision_id,
              type: 1,
              name: 'Default',
              assigned_to: null,
              sales_commission: {
                commission: 0,
                commission_type: 'percent',
                jv_broker_commission: 0,
                jv_broker_commission_type: 'percent',
              },
              trial_commission: {
                commission: 0,
                commission_type: 'percent',
                jv_broker_commission: 0,
                jv_broker_commission_type: 'percent',
              },
              rebill_commissions: [
                {
                  commission: 0,
                  no_of_payments: null,
                  commission_type: 'percent',
                  jv_broker_commission: 0,
                  jv_broker_commission_type: 'percent',
                },
              ],
            },
          ],
          is_default: 1,
          is_pinned: true,
          pin_priority: 0,
          sort_order: 1,
        });

        this.price_points.push(this.pricePoint);
      }

      this.edit_product = true;

      this.model.affiliates_enabled = this.product.affiliates_enabled;
      this.model.is_wizard_complete = this.product.is_wizard_complete;

      if (
        this.model.affiliate_landing_pages.length == 0 &&
        this.model.sales_page != ''
      ) {
        this.model.affiliate_landing_pages.push({
          id: this.generateNumber(),
          landing_page: this.model.sales_page,
          description: this.$t('frontend_groovesell_product_index_page'),
          assigned_to: [{ id: 0, name: this.$t('frontend_product_step9_all_affiliates') }],
        });
      }

      this.model.is_main = this.product.is_main;
      this.$loader.end('saveBasicProductInfo');
      return true;
    },

    async saveAllInfo() {
      this.$loader.start('saveAllInfo');
      await this.saveProductInfo();
      await this.savePricePointsInfo();
      if (
        this.model.affiliate_landing_pages.length == 0 &&
        this.model.sales_page != ''
      ) {
        this.model.affiliate_landing_pages.push({
          id: this.generateNumber(),
          landing_page: this.model.sales_page,
          description: this.$t('frontend_groovesell_product_index_page'),
          assigned_to: [{ id: 0, name: this.$t('frontend_product_step9_all_affiliates') }],
        });
      }

      this.$loader.end('saveAllInfo');
      return true;
    },

    generateNumber() {
      const min = 1;
      const max = 10000;
      return Math.floor(Math.random() * (max - min + 1) + min);
    },

    async saveProductInfo() {
      this.$loader.start('saveProductInfo');
      this.product.funnel_id = this.funnel.id;
      this.product.name = this.model.name;
      this.product.commission_after_processing =
        this.model.commission_after_processing;
      this.product.internal_name = this.model.internal_name;
      this.product.description = this.model.description;
      this.product.type = this.model.type;
      this.product.refund_period = this.model.refund_period;
      this.product.is_support_enabled = this.model.is_support_enabled;
      this.product.support_info_type = this.model.support_info_type;
      this.product.support_info_value = this.model.support_info_value;
      this.product.product_customization_details =
        this.model.product_customization_details;
      this.product.password_options = this.model.password_options;
      this.product.disable_branding_bar = this.model.disable_branding_bar;
      this.product.waiting_period = this.model.waiting_period;
      this.product.sales_page = this.model.sales_page;
      this.product.jv_page = this.model.jv_page;
      this.product.paypal_risk_percentage = this.model.paypal_risk_percentage;
      this.product.credit_card_processing_accounts =
        this.model.credit_card_processing_accounts;
      this.product.paypal_processing_accounts =
        this.model.paypal_processing_accounts;
      this.product.checkout_page_options = this.model.checkout_page_options;

      if (
        this.product.checkout_page_options.look_and_feel.template_type !=
        undefined &&
        this.product.checkout_page_options.look_and_feel.template_type == 4
      ) {
        this.product.checkout_page_options.look_and_feel = {
          template_type:
            this.product.checkout_page_options.look_and_feel.template_type,
          custom_color: {
            primary_color: this.model.primary_color
              ? this.model.primary_color
              : '#d9237f',
            secondary_color: this.model.secondary_color
              ? this.model.secondary_color
              : '#ffffff',
          },
        };
      } else {
        this.product.checkout_page_options.look_and_feel = {
          template_type:
            this.product.checkout_page_options.look_and_feel.template_type,
        };
      }

      this.product.affiliate_registration_integrations =
        this.model.affiliate_registration_integrations;
      this.product.affiliate_landing_pages = this.model.affiliate_landing_pages;
      this.product.affiliates_enabled = this.model.affiliates_enabled;
      this.product.is_wizard_complete = this.model.is_wizard_complete;
      this.product.affiliate_data_share_details =
        this.model.affiliate_data_share_details;

      if (this.product.price_points.length > 0) {
        const modelsById = new Map(
          this.product.price_points.map(pp => [String(pp.id), pp])
        );
        for (let i = 0; i < this.model.pricing_points.length; i++) {
          const plain = this.model.pricing_points[i];
          const target =
            modelsById.get(String(plain.id)) || this.product.price_points[i];
          if (!target) continue;
          target.minimum_quantity = plain.minimum_quantity
            ? plain.minimum_quantity
            : 2;
          target.maximum_quantity = plain.maximum_quantity
            ? plain.maximum_quantity
            : 2;
          target.quantity_enable =
            plain.quantity_enable;
          target.offer_bump_enable =
            plain.offer_bump_enable;

          target.offer_bump_options =
            plain.offer_bump_options;

          //
          target.post_sale_order_bump_options.post_order_heading_line =
            plain.post_sale_order_bump_options.post_order_heading_line;
          target.post_sale_order_bump_options.post_order_subheading =
            plain.post_sale_order_bump_options.post_order_subheading;
          target.post_sale_order_bump_options.post_order_description =
            plain.post_sale_order_bump_options.post_order_description;

          target.post_sale_order_bump_options.post_order_video_details =
            plain.post_sale_order_bump_options.post_order_video_details;

          target.post_sale_order_bump_options.post_order_bump_enable =
            plain.post_sale_order_bump_options.post_order_bump_enable;
          target.post_sale_order_bump_options.post_order_display_thumbnail =
            plain.post_sale_order_bump_options.post_order_display_thumbnail;
          target.post_sale_order_bump_options.post_order_thumbnail_image =
            plain.post_sale_order_bump_options.post_order_thumbnail_image;
          target.post_sale_order_bump_options.post_order_is_square =
            plain.post_sale_order_bump_options.post_order_is_square;
          target.post_sale_order_bump_options.post_order_thumbnail_size =
            plain.post_sale_order_bump_options.post_order_thumbnail_size;
          target.post_sale_order_bump_options.post_order_style_id =
            plain.post_sale_order_bump_options.post_order_style_id;
          target.post_sale_order_bump_options.post_order_product_id =
            plain.post_sale_order_bump_options.post_order_product_id;
          target.post_sale_order_bump_options.post_order_price_point_id =
            plain.post_sale_order_bump_options.post_order_price_point_id;
        }
      }

      await this.product.save();

      this.model.funnel_id = this.product.funnel_id;
      this.model.name = this.product.name;
      this.model.internal_name = this.product.internal_name;
      this.model.description = this.product.description;
      this.model.type = this.product.type;
      this.model.refund_period = this.product.refund_period;
      this.model.is_support_enabled = this.product.is_support_enabled;
      this.model.support_info_type = this.product.support_info_type;
      this.model.support_info_value = this.product.support_info_value;
      this.model.waiting_period = this.product.waiting_period;
      this.model.sales_page = this.product.sales_page;
      this.model.jv_page = this.product.jv_page;
      this.model.product_customization_details =
        this.product.product_customization_details;
      this.model.password_options = this.product.password_options;
      this.model.disable_branding_bar = this.product.disable_branding_bar;
      this.model.affiliates_enabled = this.product.affiliates_enabled;
      this.model.is_wizard_complete = this.product.is_wizard_complete;
      this.model.affiliate_landing_pages = this.product.affiliate_landing_pages;
      this.model.affiliate_data_share_details =
        this.product.affiliate_data_share_details;

      if (
        this.model.affiliate_landing_pages.length == 0 &&
        this.model.sales_page != ''
      ) {
        this.model.affiliate_landing_pages.push({
          id: this.generateNumber(),
          landing_page: this.model.sales_page,
          description: this.$t('frontend_groovesell_product_index_page'),
          assigned_to: [{ id: 0, name: this.$t('frontend_product_step9_all_affiliates') }],
        });
      }
      this.model.is_main = this.product.is_main;
      this.$loader.end('saveProductInfo');
    },

    async savePricePointsInfo() {
      this.$loader.start('savePricePointsInfo');
      const promises = [];

      const plainById = new Map(
        this.model.pricing_points.map(p => [String(p.id), p])
      );

      for (let i = 0; i < this.price_points.length; i++) {
        const plain =
          plainById.get(String(this.price_points[i].id)) ||
          this.model.pricing_points[i];
        if (!plain) continue;

        let pixelId = plain.pixel_id;
        if (plain.facebook_conversion_api_enabled == 0) {
          pixelId = 0;
        }

        this.price_points[i].offer_bump_enable =
          plain.offer_bump_enable;
        this.price_points[i].offer_bump_options =
          plain.offer_bump_options;
        this.price_points[i].quantity_enable =
          plain.quantity_enable;
        this.price_points[i].maximum_quantity =
          plain.maximum_quantity;
        this.price_points[i].minimum_quantity =
          plain.minimum_quantity;
        this.price_points[i].type = plain.type;
        this.price_points[i].name = plain.name;
        this.price_points[i].internal_name =
          plain.internal_name;
        this.price_points[i].description =
          plain.description;
        this.price_points[i].price = plain.price;
        this.price_points[i].recurring_interval =
          plain.recurring_interval;
        this.price_points[i].recurrence_interval =
          plain.recurrence_interval;
        this.price_points[i].number_of_installments =
          plain.number_of_installments;
        this.price_points[i].coupons_enabled = plain.coupons_enabled;
        this.price_points[i].is_trial = plain.is_trial;
        this.price_points[i].trial_price =
          plain.trial_price;
        this.price_points[i].trial_period =
          plain.trial_period;
        this.price_points[i].trial_period_type =
          plain.trial_period_type;
        this.price_points[i].salespage_type =
          plain.salespage_type;
        this.price_points[i].salespage_details =
          plain.salespage_details;
        this.price_points[i].post_checkout_type =
          plain.post_checkout_type;
        this.price_points[i].post_checkout_details =
          plain.post_checkout_details;
        this.price_points[i].post_transaction_details =
          plain.post_transaction_details;
        this.price_points[i].post_sale_notification_type =
          plain.post_sale_notification_type;
        this.price_points[i].post_sale_details =
          plain.post_sale_details;

        this.price_points[i].post_sale_integrations =
          plain.post_sale_integrations;
        this.price_points[i].memberships =
          plain.memberships;
        this.price_points[i].file_memberships =
          plain.file_memberships;
        this.price_points[i].legal_options =
          plain.legal_options;
        this.price_points[i].abandon_cart_integrations =
          plain.abandon_cart_integrations;
        this.price_points[i].affiliate_registration_integrations =
          plain.affiliate_registration_integrations;
        this.price_points[i].customer_portal_details =
          plain.customer_portal_details;

        this.price_points[i].content_access_type =
          plain.content_access_type;
        this.price_points[i].content_access_options =
          plain.content_access_options;
        this.price_points[i].advanced_tracking_code_enabled =
          plain.advanced_tracking_code_enabled;


        this.price_points[i].facebook_conversion_api_enabled =
          plain.facebook_conversion_api_enabled;

        this.price_points[i].pixel_id = pixelId;

        this.price_points[i].advanced_tracking_code_details =
          plain.advanced_tracking_code_details;

        this.price_points[i].pricing_string =
          plain.pricing_string;

        this.price_points[i].commission_type =
          plain.commission_type;
        this.price_points[i].commission_details =
          plain.commission_details;
        this.price_points[i].is_default =
          plain.is_default;
        this.price_points[i].is_pinned = normalizePricePoint(plain).is_pinned;
        this.price_points[i].pin_priority = normalizePricePoint(plain).pin_priority;
        this.price_points[i].sort_order = normalizePricePoint(plain).sort_order;
        promises.push(this.price_points[i].save());
      }

      if (
        this.model.affiliate_landing_pages.length == 0 &&
        this.model.sales_page != ''
      ) {
        this.model.affiliate_landing_pages.push({
          id: this.generateNumber(),
          landing_page: this.model.sales_page,
          description: this.$t('frontend_groovesell_product_index_page'),
          assigned_to: [{ id: 0, name: this.$t('frontend_product_step9_all_affiliates') }],
        });
      }

      const self = this;

      await Promise.all(promises).then((values) => {
        self.$loader.end('savePricePointsInfo');
      });

      return true;
    },
    validateStep(name) {
      const refToValidate = this.$refs[name];
      return refToValidate.validate();
    },

    saveCurrentWizardStep(name) {
      if (name == 'info-step') {
        return this.saveBasicProductInfo();
      }

      if (name == 'gateways-step' || name == 'proof-step') {
        return this.saveProductInfo();
      }

      return this.saveAllInfo();
    },

    async stepValidationSuccessful(name, isValid) {
      this.markWizardStepVisited(name);

      if (!isValid) {
        if (!this.errorTabs.includes(name)) {
          this.errorTabs.push(name);
        }
        this.validationNoticeTab = name;
        this.validationNoticeMessage = 'Review the highlighted fields, then select Save again. You can still use Back or another tab without losing the values currently on screen.';
        this.pendingAdvance = false;
        this.focusFirstInvalidControl();
        return false;
      }

      if (name == 'proof-step' && this.model.is_wizard_complete == 0) {
        this.model.is_wizard_complete = 1;
      }

      const saved = await this.persistProductChanges(
        () => this.saveCurrentWizardStep(name)
      );

      if (!saved) {
        this.pendingAdvance = false;
        return false;
      }

      const shouldAdvance = this.pendingAdvance;
      const destinationTab = this.postValidationTab;
      this.markWizardStepSaved(name);
      this.validationNoticeTab = '';
      this.validationNoticeMessage = '';
      await this.validateAllTabs(false);
      this.pendingAdvance = false;

      if (shouldAdvance && destinationTab !== name) {
        this.currentTab = destinationTab;
        this.markWizardStepVisited(destinationTab);
      }

      return true;
    },
  },
};
</script>

<style>
.gs-product-wizard {
  --gs-product-wizard-content-width: 820px;
  min-height: 100vh;
  background: var(--gs-background);
  color: var(--gs-foreground);
}

.gs-product-wizard-shell__back {
  display: flex;
  align-items: center;
  min-height: 64px;
  gap: 10px;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
  line-height: 1;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.gs-product-wizard-shell__back .gs-icon {
  color: var(--gs-muted-foreground);
}

.gs-product-wizard-shell__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 64px;
}

.gs-product-wizard__load-error {
  display: grid;
  place-items: center;
  min-height: calc(100vh - 96px);
  padding: 40px 20px;
}

.gs-product-wizard__load-error .gs-card {
  width: min(560px, 100%);
  padding: 32px;
  text-align: center;
}

.gs-product-wizard__load-error-icon {
  display: grid;
  place-items: center;
  width: 52px;
  height: 52px;
  margin: 0 auto 18px;
  color: var(--gs-danger, #dc2626);
  background: rgba(201, 54, 91, 0.1);
  border-radius: 50%;
}

.gs-product-wizard__load-error h1 {
  margin: 0;
  color: var(--gs-foreground);
  font-size: 24px;
  font-weight: 700;
}

.gs-product-wizard__load-error p {
  max-width: 440px;
  margin: 12px auto 24px;
  color: var(--gs-muted-foreground);
  line-height: 1.6;
}

.gs-product-wizard__load-error-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
}

.gs-product-wizard__body {
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: 44px;
  max-width: 1260px;
  margin: 0 auto;
  padding: 32px 28px 56px;
}

.gs-product-wizard__sidebar {
  position: sticky;
  top: 96px;
  align-self: start;
  padding-top: 0;
}

.gs-product-wizard__nav-group + .gs-product-wizard__nav-group {
  margin-top: 28px;
}

.gs-product-wizard__nav-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 0 8px 12px;
  color: var(--gs-muted-foreground);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  line-height: 1;
  text-transform: uppercase;
}

.gs-product-wizard__group-progress {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 34px;
  margin-right: 12px;
  color: var(--gs-muted-foreground);
  font-size: 11px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0;
  line-height: 1;
}

.gs-product-wizard__group-progress.is-complete {
  color: var(--gs-success);
}

.gs-product-wizard__group-progress.has-error {
  color: var(--gs-danger);
}

.gs-product-wizard__nav-item {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 38px;
  padding: 6px 12px;
  color: var(--gs-muted-foreground);
  font-size: 15px;
  font-weight: 500;
  line-height: 1.25;
  text-align: left;
  background: transparent;
  border: 0;
  border-left: 3px solid transparent;
  border-radius: var(--gs-radius-sm);
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}

.gs-product-wizard__nav-item:hover {
  color: var(--gs-foreground);
  background: var(--gs-muted);
}

.gs-product-wizard__nav-item.is-active {
  color: var(--gs-foreground);
  font-weight: 700;
  background: var(--gs-primary-soft);
  border-left-color: var(--gs-primary);
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.gs-product-wizard__nav-item.has-error {
  color: var(--gs-danger);
}

.gs-product-wizard__nav-dot {
  display: inline-block;
  flex: 0 0 auto;
  width: 10px;
  height: 10px;
  margin-right: 12px;
  border: 1.5px solid var(--gs-border-strong);
  border-radius: 999px;
  background: transparent;
}

.gs-product-wizard__nav-item.is-active .gs-product-wizard__nav-dot {
  border-color: var(--gs-primary);
}

.gs-product-wizard__nav-item.is-complete .gs-product-wizard__nav-dot {
  border-color: var(--gs-success);
  background: var(--gs-success);
}

.gs-product-wizard__nav-item.has-error .gs-product-wizard__nav-dot {
  border-color: var(--gs-danger);
  background: var(--gs-danger);
}

.gs-product-wizard__nav-action {
  margin-top: 4px;
}

.gs-product-wizard__nav-dot--action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  margin-right: 10px;
  margin-left: -2px;
  color: var(--gs-primary);
  border: 0;
  background: transparent;
}

.gs-product-wizard__main {
  min-width: 0;
}

.gs-product-wizard__intro {
  margin-bottom: 18px;
  text-align: center;
}

.gs-product-wizard__step-count {
  display: block;
  margin-bottom: 7px;
  color: var(--gs-primary);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.gs-product-wizard__intro p {
  margin: 8px 0 0;
  color: var(--gs-muted-foreground);
  font-size: 15px;
  line-height: 1.5;
}

.gs-product-wizard__canvas {
  width: 100%;
  max-width: var(--gs-product-wizard-content-width);
  margin: 0 auto;
}

.gs-product-wizard__validation-notice {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  max-width: var(--gs-product-wizard-content-width);
  margin: 0 auto 16px;
  padding: 14px 16px;
  color: #7b2039;
  background: #fff5f7;
  border: 1px solid #f2b8c7;
  border-radius: 8px;
}

.gs-product-wizard__validation-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  color: #ffffff;
  background: var(--gs-danger);
  border-radius: 999px;
}

.gs-product-wizard__validation-notice strong {
  display: block;
  font-size: 14px;
  font-weight: 700;
}

.gs-product-wizard__validation-notice p {
  margin: 4px 0 0;
  font-size: 13px;
  line-height: 1.5;
}

#gs-product-wizard-root .gs-pricing-step,
#gs-product-wizard-root .gs-gateways-step,
#gs-product-wizard-root .gs-contents-step,
#gs-product-wizard-root .gs-checkout-step,
#gs-product-wizard-root .gs-tracking-step,
#gs-product-wizard-root .gs-bumps-step,
#gs-product-wizard-root .gs-funnel-step,
#gs-product-wizard-root .gs-fulfillment-step,
#gs-product-wizard-root .gs-affiliates-step,
#gs-product-wizard-root .gs-proof-step,
#gs-product-wizard-root .gs-finish-step {
  width: 100%;
  max-width: none;
  margin-right: 0;
  margin-left: 0;
}

/* The single source of truth for every step's summary card. */
#gs-product-wizard-root .gs-wizard-card {
  padding: 20px 24px;
  color: var(--gs-foreground);
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

#gs-product-wizard-root .gs-wizard-card > h2:first-child,
#gs-product-wizard-root .gs-wizard-card > header:first-child h2 {
  margin: 0 0 6px;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}

#gs-product-wizard-root .gs-wizard-card > p:first-of-type {
  margin: 0 0 18px;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.55;
}

#gs-product-wizard-root .gs-product-wizard__intro h1,
#gs-product-wizard-root .gs-product-wizard__title,
#gs-product-wizard-root #gs-product-wizard-title {
  margin: 0;
  color: var(--gs-foreground);
  font-size: 24px;
  font-weight: 650;
  line-height: 1.3;
  letter-spacing: 0;
}

.gs-product-wizard__canvas > div,
.gs-product-wizard__canvas > div > div {
  width: 100%;
}

.gs-product-wizard__canvas .bg-white {
  border-radius: var(--gs-radius-sm);
  border: 1px solid var(--gs-border);
  box-shadow: var(--gs-shadow-sm);
}

.gs-product-wizard__canvas .rounded {
  border-radius: var(--gs-radius-sm);
}

.gs-product-wizard__canvas .w-1\/2,
.gs-product-wizard__canvas .w-2\/5,
.gs-product-wizard__canvas .w-3\/5,
.gs-product-wizard__canvas .w-4\/5 {
  width: 100% !important;
}

.gs-product-wizard__canvas .bg-white.rounded,
.gs-product-wizard__canvas .bg-white.rounded.p-4,
.gs-product-wizard__canvas .bg-white.rounded.px-4 {
  padding: 18px 20px !important;
  border: 1px solid var(--gs-border) !important;
  border-radius: var(--gs-radius-sm) !important;
  box-shadow: var(--gs-shadow-sm) !important;
}

.gs-product-wizard__canvas .border-grey-100.border,
.gs-product-wizard__canvas .border.border-grey-100 {
  margin: 4px 0 18px !important;
  padding: 0 !important;
  border: 0 !important;
  background: transparent !important;
}

.gs-product-wizard__canvas .gs-default-tabs {
  display: inline-flex !important;
  gap: 6px !important;
  padding: 0 !important;
  border: 0 !important;
}

.gs-product-wizard__canvas .gs-default-tabs-item {
  min-height: auto !important;
  padding: 8px 12px !important;
  margin: 0 !important;
  color: var(--gs-muted-foreground) !important;
  font-size: 14px !important;
  font-weight:500 !important;
  border: 1px solid var(--gs-border) !important;
  border-radius: var(--gs-radius-sm) !important;
  background: var(--gs-surface) !important;
}

.gs-product-wizard__canvas .gs-default-tabs-item-active {
  color: var(--gs-active) !important;
  background: var(--gs-active-soft) !important;
  border-color: var(--gs-active) !important;
  box-shadow: 0 0 0 3px var(--gs-active-shadow) !important;
}

.gs-product-wizard__canvas label {
  color: var(--gs-foreground) !important;
  font-size: 15px !important;
  font-weight: 600 !important;
}

.gs-product-wizard__canvas input,
.gs-product-wizard__canvas textarea {
  color: var(--gs-foreground) !important;
  font-size: 16px !important;
  border-color: var(--gs-border) !important;
  border-radius: var(--gs-radius-sm) !important;
}

.gs-product-wizard__canvas button {
  font-family: inherit;
}

.gs-product-wizard__canvas button:focus-visible,
.gs-product-wizard__nav-item:focus-visible,
.gs-product-wizard-shell__back:focus-visible {
  outline: 3px solid var(--gs-active-shadow);
  outline-offset: 2px;
}

.gs-product-wizard__footer-actions {
  display: grid;
  grid-template-columns: minmax(120px, 1fr) auto minmax(240px, 1fr);
  align-items: center;
  gap: 10px;
  width: 100%;
  max-width: var(--gs-product-wizard-content-width);
  margin: 20px auto 0;
  padding-top: 18px;
  border-top: 1px solid var(--gs-border);
}

.gs-product-wizard__footer-start {
  justify-self: start;
}

.gs-product-wizard__footer-end {
  display: flex;
  justify-self: end;
  gap: 10px;
}

.gs-product-wizard__save-status {
  display: inline-flex;
  align-items: center;
  justify-self: center;
  gap: 7px;
  color: var(--gs-muted-foreground);
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.gs-product-wizard__save-status > span {
  width: 8px;
  height: 8px;
  background: var(--gs-success);
  border-radius: 999px;
}

.gs-product-wizard__save-status > span.is-dirty {
  background: #d89614;
}

.gs-product-wizard__drawer-intro {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 22px;
}

.gs-product-wizard__drawer-intro p {
  margin: 0;
  color: var(--gs-muted-foreground);
  font-size: 15px;
  line-height: 1.5;
}

.gs-product-wizard__checkout-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.gs-product-wizard__checkout-divider {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  border: 1px dashed var(--gs-border-strong, #cbd5e1);
  border-radius: 6px;
  background: var(--gs-surface-subtle, #f8fafc);
}

.gs-product-wizard__checkout-divider-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 0;
  color: var(--gs-foreground, #182230);
  font: inherit;
  font-size: 14px;
  font-weight: 650;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.gs-product-wizard__checkout-divider small {
  color: var(--gs-muted-foreground, #667085);
  font-size: 12px;
}

.gs-product-wizard__checkout-card {
  padding: 18px;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: 6px;
  box-shadow: var(--gs-shadow-sm);
}

.gs-product-wizard__checkout-card strong {
  display: block;
  margin-bottom: 8px;
  color: var(--gs-foreground);
  font-size: 16px;
  font-weight: 600;
  line-height: 1.35;
}

.gs-product-wizard__checkout-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.gs-product-wizard__checkout-type {
  flex: 0 0 auto;
  padding: 5px 9px;
  border-radius: 999px;
  background: var(--gs-active-soft);
  color: var(--gs-active);
  font-size: 12px;
  font-weight: 700;
}

.gs-product-wizard__checkout-card span {
  display: block;
  overflow-wrap: anywhere;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.45;
}

.gs-product-wizard__checkout-metadata {
  display: grid;
  gap: 8px;
  margin-top: 14px;
  padding: 12px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: 6px;
}

.gs-product-wizard__checkout-metadata span {
  overflow-wrap: anywhere;
}

.gs-product-wizard__checkout-metadata small {
  display: block;
  margin-bottom: 3px;
  color: var(--gs-muted-foreground);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .04em;
}

.gs-product-wizard__checkout-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
}

@media (max-width: 640px) {
  .gs-product-wizard__checkout-card-head,
  .gs-product-wizard__checkout-actions { flex-direction: column; align-items: stretch; }
}

.gs-product-wizard__shelf-empty {
  padding: 22px;
  color: var(--gs-muted-foreground);
  font-size: 15px;
  line-height: 1.5;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: 6px;
}

.gs-product-wizard__shelf-empty h3 {
  margin: 0 0 8px;
  color: var(--gs-foreground);
  font-size: 18px;
  font-weight: 600;
}

.gs-product-wizard__shelf-empty p {
  margin: 0 0 16px;
}

#gs-product-wizard-root .gs-modal__body h3 {
  font-size: 16px;
  font-weight: 650;
}

@media (max-width: 900px) {
  .gs-product-wizard__body {
    grid-template-columns: 1fr;
    gap: 18px;
    padding: 24px 16px 36px;
  }

  .gs-product-wizard__sidebar {
    position: static;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 18px;
    padding-top: 0;
  }

  .gs-product-wizard__nav-group + .gs-product-wizard__nav-group {
    margin-top: 0;
  }
}

@media (max-width: 640px) {
  .gs-product-wizard-shell__actions {
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .gs-product-wizard__sidebar {
    grid-template-columns: 1fr;
  }
}

.gs-default-tabs-item:focus {
  outline: none !important;
  outline-style: none !important;
}

.invalid .ql-toolbar.ql-snow {
  border-bottom-color: var(--gs-danger);
}

.invalid .ql-container {
  border-color: var(--gs-danger) !important;
}
</style>
